
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import api, { BACKEND_URL } from '../api/axios';

// Maps a notification's "type" (whatever your backend sends) to an
// icon + color, mirroring the status colors already used on
// MyApplications.jsx. Adjust these keys if your backend uses
// different type names.
const typeStyles = {
  applied: { icon: '📨', color: 'bg-[#EFF6FF] text-[#2563EB]' },
  new_applicant: { icon: '🧑‍💼', color: 'bg-[#EFF6FF] text-[#2563EB]' },
  viewed: { icon: '👀', color: 'bg-[#FFF7ED] text-[#F59E0B]' },
  shortlisted: { icon: '✅', color: 'bg-[#ECFDF5] text-[#10B981]' },
  interview: { icon: '📅', color: 'bg-[#FFF7ED] text-[#F59E0B]' },
  selected: { icon: '🎉', color: 'bg-[#ECFDF5] text-[#10B981]' },
  rejected: { icon: '❌', color: 'bg-red-100 text-red-600' },
  default: { icon: '🔔', color: 'bg-gray-100 text-gray-600' },
};

function timeAgo(dateStr) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Poll every 30s so status changes (shortlisted, rejected, new
    // applicants, etc.) show up without a full page refresh.
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Lock background scroll while the panel is open on mobile, where it renders
  // as a fixed full-width sheet rather than a small anchored dropdown.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    if (window.innerWidth < 640) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const markAllRead = async () => {
    const previous = notifications;
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
    );
    try {
      await api.post('/notifications/mark-all-read');
    } catch (err) {
      console.error('Failed to mark all as read', err);
      setNotifications(previous); // revert on failure
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read_at) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      try {
        await api.put(`/notifications/${notification.id}/read`);
      } catch (err) {
        console.error('Failed to mark notification as read', err);
      }
    }
    setOpen(false);

    // Employer notification: a job seeker applied -> go to that job's applicants list
    if (notification.type === 'new_applicant' && notification.job_posting_id) {
      navigate(`/employer/jobs/${notification.job_posting_id}/applicants`);
      return;
    }

    // Job seeker notification: their application status changed -> go to their applications list
    if (notification.application_id || notification.job_application_id) {
      navigate('/my-applications');
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 hover:bg-white/10 rounded-full"
        aria-label="Notifications"
      >
        <FaBell className="text-xl text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[#C7F464] text-[#17142A] text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop — mobile only, so tapping outside the sheet closes it and
              the page behind reads as dimmed rather than still-interactive */}
          <div
            className="fixed inset-0 bg-black/20 z-40 sm:hidden"
            onClick={() => setOpen(false)}
          />

          {/* On mobile this renders as a fixed sheet pinned under the navbar,
              spanning the viewport width so a w-80 dropdown never overflows a
              narrow screen or gets clipped off the right edge. From sm: up it
              reverts to the original small anchored dropdown. */}
          <div
            className="fixed sm:absolute inset-x-3 sm:inset-x-auto top-16 sm:top-auto right-auto sm:right-0 sm:mt-2 w-auto sm:w-80 max-h-[75vh] sm:max-h-96 bg-white border rounded-xl shadow-lg overflow-hidden z-50"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h4 className="font-semibold text-[#252438] text-sm">Notifications</h4>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-[#7047FF] font-medium hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="overflow-y-auto max-h-[calc(75vh-48px)] sm:max-h-80">
              {loading ? (
                <p className="text-sm text-gray-400 text-center py-6">Loading...</p>
              ) : notifications.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  No notifications yet.
                </p>
              ) : (
                notifications.map((n) => {
                  const style = typeStyles[n.type] || typeStyles.default;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleNotificationClick(n)}
                      className={`w-full text-left flex gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                        !n.read_at ? 'bg-[#EFF6FF]/40' : ''
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0 ${style.color}`}>
                        {style.icon}
                      </span>
                      <span className="min-w-0">
                        <p className="text-sm text-[#252438] leading-snug">
                          {n.message}
                        </p>
                        <span className="text-xs text-gray-400">{timeAgo(n.created_at)}</span>
                      </span>
                      {!n.read_at && (
                        <span className="w-2 h-2 rounded-full bg-[#7047FF] shrink-0 mt-1.5" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}